import React, { useEffect, useRef, useState } from "react";
import { useDebounce } from "use-debounce";
import { FileOutlined, FileTextOutlined } from "@ant-design/icons";

import { useDispatch } from "react-redux";
import { Table, Pagination, Checkbox, Typography, Row } from "antd";
import { useConfirmModal } from "components/Modal/ConfirmModal";
import { useNotesModal } from "components/Modal/NotesModal";

import { Button } from "antd";

// == Import SCSS
import "./ListKeyCards.less";

// == import i18n
import { useTranslation } from "react-i18next";

// == import component
import { Input, message } from "antd";
import {
  listKeyCards,
  lostKeyCards,
  releaseKeyCards,
  saveKeyCardsNote,
} from "services/features/VisitsSlice";
import moment from "moment";

const { Search } = Input;
const { Text } = Typography;

const ListKeyCards = () => {
  const [t] = useTranslation();
  const dispatch = useDispatch();

  const searchRef = useRef();

  const [searchText, setSearchText] = useState("");
  const [debouncedSearchText] = useDebounce(searchText, 500);
  const [owner, setOwner] = useState(null);
  const [searching, setSearching] = useState(false);
  const [keyCards, setKeyCards] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [pageCount, setPageCount] = useState(0);
  const [onlyWarning, setOnlyWarning] = useState(false);
  const [hideLost, setHideLost] = useState(true);

  const [selectedKeyCards, setSelectedKeyCards] = useState([]);
  const [askUserConfirm, ConfirmModal] = useConfirmModal();
  const [updateNotesModal, NotesModal] = useNotesModal();

  const load = async () => {
    setSearching(true);
    const kc = await dispatch(
      listKeyCards(debouncedSearchText, page, pageSize, onlyWarning, hideLost),
    );
    setPage(kc.page);
    setPageSize(kc.pageSize);
    setPageCount(kc.pageCount);
    setSearching(false);

    setKeyCards(
      kc.items.map(kc => ({
        ref: kc.keycard,
        owner: kc.visitor.firstname + " " + kc.visitor.lastname,
        status: kc.status,
        notes: kc.notes,
        warning: kc.warning,
        date: moment(kc.date).format("DD/MM/YYYY HH:mm"),
      })),
    );
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, onlyWarning, hideLost]);

  useEffect(() => {
    setPage(1);
    setSelectedKeyCards([]);
    load();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchText]);

  const handleRelease = async () => {
    if (
      await askUserConfirm(
        t("ReleaseKeyCardConfirmationTitle"),
        selectedKeyCards.length > 1
          ? t("ReleaseKeyCardsConfirmationText")
          : t("ReleaseKeyCardConfirmationText"),
      )
    ) {
      const markAsExited = await askUserConfirm(
        t("VisitorLeaveConfirmationTitle"),
        selectedKeyCards.length > 1
          ? t("VisitorsLeaveConfirmationText")
          : t("VisitorLeaveConfirmationText"),
      );

      try {
        await dispatch(releaseKeyCards(selectedKeyCards, markAsExited));
        message.success(t("KeyCardReleased"));
        setPage(1);
        setSearchText("");
        setSelectedKeyCards([]);
      } catch (error) {
        message.error(t("KeyCardReleaseFailed"));
      }

      load();
    }
  };

  const handleLost = async () => {
    if (
      await askUserConfirm(
        t("LostKeyCardConfirmationTitle"),
        selectedKeyCards.length > 1
          ? t("LostKeyCardsConfirmationText")
          : t("LostKeyCardConfirmationText"),
      )
    ) {
      try {
        await dispatch(lostKeyCards(selectedKeyCards));
        message.success(t("LostCardSuccess"));
        setPage(1);
        setSearchText("");
        setSelectedKeyCards([]);
      } catch (error) {
        message.error(t("LostCardFailed"));
      }

      load();
    }
  };

  const handleUpdateNotes = async keycard => {
    try {
      setSearching(true);
      const newNotes = await updateNotesModal(t("UpdateNotes"), keycard.notes);
      await dispatch(saveKeyCardsNote(keycard.ref, newNotes));
    } catch (e) {
      message.error(t("FailedToUpdateNotes"));
    }
    setSearching(false);
    load();
  };

  const columns = [
    {
      title: "",
      key: "action",
      render: (text, record) => (
        <Checkbox
          checked={selectedKeyCards.includes(record.ref)}
          onChange={v => {
            if (v.target.checked) {
              setSelectedKeyCards([...selectedKeyCards, record.ref]);
            } else {
              setSelectedKeyCards(selectedKeyCards.filter(r => r !== record.ref));
            }
          }}
        />
      ),
    },
    {
      title: t("KeyCardReference"),
      dataIndex: "ref",
      key: "ref",
      className: "listKeyCard__table__header",
      render: (text, record) => (
        <Text type={record.warning ? "danger" : undefined}>{record.ref}</Text>
      ),
    },
    {
      title: t("KeyCardVisitor"),
      dataIndex: "owner",
      key: "owner",
      className: "listKeyCard__table__header",
      render: (text, record) => (
        <Text type={record.warning ? "danger" : undefined}>{record.owner}</Text>
      ),
    },
    {
      title: t("KeyCardDate"),
      dataIndex: "date",
      key: "date",
      className: "listKeyCard__table__header",
      render: (text, record) => (
        <Text type={record.warning ? "danger" : undefined}>{record.date}</Text>
      ),
    },
    {
      title: t("Status"),
      dataIndex: "status",
      key: "status",
      className: "listKeyCard__table__header",
      render: (text, record) => (
        <Text type={record.warning ? "danger" : undefined}>{t(record.status)}</Text>
      ),
    },
    {
      title: null,
      dataIndex: "notes",
      key: "status",
      className: "listKeyCard__table__header",
      render: (text, record) => (
        <Button
          type="text"
          danger={record.warning}
          icon={record.notes.length > 0 ? <FileTextOutlined /> : <FileOutlined />}
          onClick={() => handleUpdateNotes(record)}
        ></Button>
      ),
    },
  ];

  return (
    <div className="listKeyCard" style={{ height: window.innerHeight }}>
      {NotesModal}
      {ConfirmModal}
      <p className="listKeyCard_instructions" style={{ marginTop: "100px", marginBottom: "35px" }}>
        {t("KeycardReleaseInstruction", owner)}
      </p>

      <Search
        ref={searchRef}
        style={{ zIndex: 2147500009, width: "700px" }}
        placeholder={t("SearchKeyCardReference")}
        onChange={e => setSearchText(e.target.value)}
        value={searchText}
        loading={searching}
        notFoundContent={null}
      />
      <Row style={{ marginTop: "20px" }}>
        <Checkbox
          checked={onlyWarning}
          onChange={e => {
            setOnlyWarning(e.target.checked);
          }}
        >
          {t("OnlyWarning")}
        </Checkbox>
        <Checkbox
          checked={hideLost}
          onChange={e => {
            setHideLost(e.target.checked);
          }}
        >
          {t("HideLost")}
        </Checkbox>
      </Row>
      <Table
        dataSource={keyCards}
        columns={columns}
        rowKey="id"
        pagination={false}
        style={{ width: "1500px", marginTop: "50px", marginBottom: "50px" }}
      />

      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
        }}
      >
        <Pagination
          current={page}
          pageSize={pageSize}
          total={pageCount}
          style={{ marginRight: "20px" }}
          onChange={(page, pageSize) => {
            setPage(page);
            setPageSize(pageSize);
          }}
        />
        <div style={{ marginTop: "20px", display: "flex" }}>
          <Button
            type="primary"
            onClick={handleLost}
            disabled={selectedKeyCards.length === 0}
            style={{ marginRight: "10px" }}
          >
            {selectedKeyCards.length > 1 ? t("ActionLostKeyCards") : t("ActionLostKeyCard")}
          </Button>
          <Button type="primary" onClick={handleRelease} disabled={selectedKeyCards.length === 0}>
            {selectedKeyCards.length > 1 ? t("ActionReleaseKeyCards") : t("ActionReleaseKeyCard")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ListKeyCards;
